import * as React from 'react';
import type {
  BravoWriteResult,
  DeleteLineItemToolInput,
  PromoteProposalToolInput
} from 'shared/src/api/ai-line-item-chat-types';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import { CommitCardFrame, type CommitOutcome } from '@/features/ai/shared/commit-card-frame';

type MutateTool = 'deleteLineItem' | 'promoteProposal';

export type LineItemMutateCardProps = {
  tool: MutateTool;
  input: DeleteLineItemToolInput | PromoteProposalToolInput;
  proposalId: string;
  toolCallId?: string;
  /** Outcome of a resolution made outside this card (prior session, or the modal's soft-cancel). */
  externalOutcome?: CommitOutcome | null;
  onResolve: (output: BravoWriteResult) => void | Promise<void>;
};

/**
 * Generic confirmation card for the line-item management confirmed writes (deleteLineItem /
 * promoteProposal). Confirm/cancel only; each tool maps to the deterministic tRPC mutation that
 * authorizes + writes (reusing the same services the grid UI uses). Resolves the tool on every
 * outcome. Tiering: promote is always the live tier (it IS the go-live moment); delete is live
 * only when the target is promoted — determined via the proposal line-items lookup, with Confirm
 * held disabled until the lookup answers.
 */
export function LineItemMutateCard({
  tool,
  input,
  proposalId,
  toolCallId,
  externalOutcome,
  onResolve
}: LineItemMutateCardProps): JSX.Element {
  const utils = trpc.useUtils();
  const [outcome, setOutcome] = React.useState<CommitOutcome | null>(null);

  // Both hooks are declared unconditionally (rules of hooks); only the matching one is called.
  const deleteMutation = trpc.deleteLineItem.useMutation();
  const promoteMutation = trpc.promoteProposal.useMutation();
  const { data: lineItemsData } = trpc.getProposalLineItems.useQuery(
    { proposal_id: proposalId },
    { enabled: tool === 'deleteLineItem' }
  );

  const isPending = deleteMutation.isPending || promoteMutation.isPending;

  const deleteTarget =
    tool === 'deleteLineItem'
      ? lineItemsData?.data.find(li => li.id === (input as DeleteLineItemToolInput).line_item_id)
      : undefined;
  const live =
    tool === 'promoteProposal' ||
    (deleteTarget !== undefined && deleteTarget.execution_status !== 'no_execution');

  async function invalidate() {
    await utils.getProposalLineItems.invalidate();
    await utils.campaignV2.invalidate();
    await utils.proposalDiff.invalidate();
  }

  async function handleConfirm() {
    try {
      let result: BravoWriteResult;
      if (tool === 'deleteLineItem') {
        const i = input as DeleteLineItemToolInput;
        await deleteMutation.mutateAsync({ strategy_id: proposalId, line_item_id: i.line_item_id });
        result = { status: 'deleted', line_item_id: i.line_item_id };
        setOutcome({
          status: 'applied',
          summary: i.line_item_name ? `Deleted "${i.line_item_name}"` : 'Line item deleted'
        });
      } else {
        const i = input as PromoteProposalToolInput;
        await promoteMutation.mutateAsync({
          proposal_id: proposalId,
          description: i.description ?? 'Promoted via assistant'
        });
        result = { status: 'promoted' };
        setOutcome({ status: 'applied', summary: 'Promoted to execution' });
      }
      await invalidate();
      await onResolve(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to apply the change';
      setOutcome({ status: 'failed', summary: message });
      showErrorToast(message);
      // Feed the error back so the agent can explain it and re-propose a corrected change (or relay
      // guidance for partial/system failures). Nothing changed remotely, so a corrected retry is safe.
      await onResolve({ status: 'failed', error: message });
    }
  }

  async function handleCancel() {
    setOutcome({ status: 'cancelled' });
    await onResolve({ status: 'cancelled' });
  }

  return (
    <CommitCardFrame
      tier={live ? 'live' : 'staged'}
      title={heading(tool, input)}
      consequence={consequence(tool, live)}
      confirmLabel={tool === 'deleteLineItem' ? 'Delete' : 'Promote to live'}
      // Delete's tier depends on the lookup — hold Confirm until it answers.
      confirmDisabled={tool === 'deleteLineItem' && !lineItemsData}
      isPending={isPending}
      outcome={outcome}
      externalOutcome={externalOutcome ?? null}
      toolCallId={toolCallId}
      onConfirm={handleConfirm}
      onCancel={handleCancel}>
      <CardBody tool={tool} input={input} />
    </CommitCardFrame>
  );
}

function heading(tool: MutateTool, input: LineItemMutateCardProps['input']): string {
  if (tool === 'deleteLineItem') {
    const i = input as DeleteLineItemToolInput;
    return `Delete line item${i.line_item_name ? ` "${i.line_item_name}"` : ''}`;
  }
  return 'Promote proposal changes to execution';
}

function consequence(tool: MutateTool, live: boolean): string {
  if (tool === 'promoteProposal') {
    return 'Pushes all pending proposal changes live to execution.';
  }
  return live
    ? 'This line item is promoted — deleting it affects live delivery.'
    : 'Removes this line item from the proposal.';
}

function CardBody({
  tool,
  input
}: {
  tool: MutateTool;
  input: LineItemMutateCardProps['input'];
}): JSX.Element {
  if (tool === 'promoteProposal') {
    const i = input as PromoteProposalToolInput;
    return (
      <p className="text-sm text-gray-600">
        This pushes all pending proposal changes live to execution.
        {i.description ? <span className="mt-1 block italic">“{i.description}”</span> : null}
      </p>
    );
  }
  return (
    <p className="text-sm text-gray-600">
      This removes the line item from the proposal. A line item with live media plans cannot be
      deleted.
    </p>
  );
}
